import Edit from "@mui/icons-material/Edit";
import {
  Box,
  GridLegacy as Grid,
  IconButton,
  Stack,
  Tooltip,
  Typography,
  useTheme,
} from "@mui/material";
import { xsHeight, mdHeight } from "components/AppBar";
import { tabsOffset } from "components/TabLinks";
import { DynamicFieldType, Issue, ProgramArea } from "generated-graphql/graphql";
import { useCanEditFacility } from "hooks/useAuthorization";
import { useMemo, useState } from "react";
import { formatDateLikeForDisplay } from "util/dates";

import { useFacility, useStateInformationIssues } from "../useFacility";
import { EditStateInformationDialog } from "./EditStateInformationDialog";
import { V2DisplayOnlyField as DisplayOnlyField } from "./V2DisplayOnlyField";

export const STATE_INFORMATION_SECTION_ID = "state-information";
export const STATE_INFORMATION_SECTION_ICON = "🏛️";

/** Group issues by their key for per-field display */
function useIssuesByKey(issues: Issue[]) {
  return useMemo(() => {
    const map = new Map<string, Issue[]>();
    for (const issue of issues) {
      if (issue.key) {
        const existing = map.get(issue.key) ?? [];
        existing.push(issue);
        map.set(issue.key, existing);
      }
    }
    return map;
  }, [issues]);
}

function formatFieldValue(value: unknown, inputType?: DynamicFieldType | null): string {
  if (value == null) return "";
  const str = String(value);
  if (inputType === DynamicFieldType.Date && str) {
    return formatDateLikeForDisplay(str) || str;
  }
  return str;
}

export const StateInformationSection: React.FC = () => {
  const theme = useTheme();
  const { data } = useFacility();
  const facility = data?.facility;
  const canEdit = useCanEditFacility();
  const isCA = facility?.state === "CA";
  const stateFields = facility?.stateFields;

  const chemicalFields = useMemo(
    () => (stateFields ?? []).filter((f) => f.type !== ProgramArea.Rcra),
    [stateFields]
  );
  const wasteFields = useMemo(
    () => (stateFields ?? []).filter((f) => f.type === ProgramArea.Rcra),
    [stateFields]
  );

  const hasAnyFields = chemicalFields.length > 0 || wasteFields.length > 0;
  const [editingProgramArea, setEditingProgramArea] = useState<ProgramArea | null>(null);

  const issues = useStateInformationIssues();
  const issuesByKey = useIssuesByKey(issues);

  return (
    <Box
      id={STATE_INFORMATION_SECTION_ID}
      sx={{
        scrollMarginTop: {
          xs: theme.spacing(xsHeight + tabsOffset + 11),
          md: theme.spacing(mdHeight + tabsOffset + 11),
        },
        "& .edit-icon": { opacity: 0, transition: "opacity 200ms" },
        "&:hover .edit-icon": { opacity: 1 },
      }}
    >
      <Typography variant="h6" mb={5}>
        {STATE_INFORMATION_SECTION_ICON} State Information
      </Typography>

      {editingProgramArea && (
        <EditStateInformationDialog
          programArea={editingProgramArea}
          onClose={() => setEditingProgramArea(null)}
        />
      )}

      {isCA ? (
        <Typography variant="body2" color="text.secondary">
          California HMBP questions appear in the facility&apos;s Tier II report in Chemicals.
        </Typography>
      ) : !hasAnyFields ? (
        <Typography variant="body2" color="text.secondary">
          No additional state information needed.
        </Typography>
      ) : (
        <Stack spacing={4}>
          {chemicalFields.length > 0 && (
            <Box>
              <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2}>
                <Typography variant="subtitle2">Chemicals Program</Typography>
                {canEdit && (
                  <Tooltip title="Edit Section">
                    <IconButton
                      size="small"
                      className="edit-icon"
                      sx={{ color: "action.active" }}
                      onClick={() => setEditingProgramArea(ProgramArea.Epcra)}
                    >
                      <Edit fontSize="small" />
                    </IconButton>
                  </Tooltip>
                )}
              </Stack>
              <Grid container columnSpacing={3} rowSpacing={2}>
                {chemicalFields.map((field) => (
                  <Grid item xs={12} md={6} key={field.key}>
                    <DisplayOnlyField
                      label={
                        <span style={{ whiteSpace: "normal" }}>{field.label ?? field.key}</span>
                      }
                      text={formatFieldValue(field.value, field.inputType)}
                      issues={issuesByKey.get(field.key)}
                    />
                  </Grid>
                ))}
              </Grid>
            </Box>
          )}

          {wasteFields.length > 0 && (
            <Box>
              <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2}>
                <Typography variant="subtitle2">Waste Program</Typography>
                {canEdit && (
                  <Tooltip title="Edit Section">
                    <IconButton
                      size="small"
                      className="edit-icon"
                      sx={{ color: "action.active" }}
                      onClick={() => setEditingProgramArea(ProgramArea.Rcra)}
                    >
                      <Edit fontSize="small" />
                    </IconButton>
                  </Tooltip>
                )}
              </Stack>
              <Grid container columnSpacing={3} rowSpacing={2}>
                {wasteFields.map((field) => (
                  <Grid item xs={12} md={6} key={field.key}>
                    <DisplayOnlyField
                      label={
                        <span style={{ whiteSpace: "normal" }}>{field.label ?? field.key}</span>
                      }
                      text={formatFieldValue(field.value, field.inputType)}
                      issues={issuesByKey.get(field.key)}
                    />
                  </Grid>
                ))}
              </Grid>
            </Box>
          )}
        </Stack>
      )}
    </Box>
  );
};
